import React from "react";
import Typography from "@insite/mobius/Typography";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import parse from "html-react-parser";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button from "@insite/mobius/Button";
import { css } from "styled-components";

const enum fields {
    image = "image",
    imageSize = "imageSize",
    backgroundColor = "backgroundColor",
    buttonLink = "buttonLink",
    buttonLabel = "buttonLabel",
    heading = "heading",
    subheading = "subheading",
    buttonAlignment = "buttonAlignment",
    buttonBackground = "buttonBackground",
    buttonWidth = "buttonWidth",
}

interface Props extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.imageSize]: string;
        [fields.backgroundColor]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonLabel]: string;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.buttonAlignment]: string;
        [fields.buttonBackground]: string;
        [fields.buttonWidth]: string;
    }
}

const CustomBanner: React.FC<Props> = (props) => {

    //Left card style
    const cardWrapperStyles = {
            css: css`
                background-color: ${props.fields.backgroundColor};
                max-width: 50%;
                box-shadow: 1px 1px 1px 1px #888888;
                border: 5px solid white;
                padding: 2rem;
            `,
    };

    //Button style
    const buttonStyles = {
        css: css`
            width: 100%;
            display:inline-block;
            background: ${props.fields.buttonBackground};
            border: 2px solid white;
            width: ${props.fields.buttonWidth};
            justify-content: ${props.fields.buttonAlignment ?? "left"};
            margin-bottom: 1rem;       
            `,
    };

    return (
        <StyledWrapper > 
            <StyledWrapper {...cardWrapperStyles}>
            
                <Typography>{parse(props.fields.heading, parserOptions)}</Typography>
                <Button {...buttonStyles}>
                    {props.fields.buttonLabel}
                </Button>
                <Typography>{parse(props.fields.subheading, parserOptions)}</Typography>
            </StyledWrapper>
        </StyledWrapper>
    )
}

const widgetModule: WidgetModule = {
    component: CustomBanner,
    definition: {
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                displayName: "Banner Background image",
                tooltip: "Image Size has to be 330 x 229",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.imageSize,
                editorTemplate: "TextField",
                displayName: "Banner Background Recommended Image Size",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Card Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
            },
            {
                fieldType: "Translatable",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Card Subheading",
                defaultValue: "",
                extendedConfig: { height: 170 },
            },
            {
                fieldType: "General",
                name: fields.buttonBackground,
                editorTemplate: "ColorPickerField",
                displayName: "Button Background Color",
                defaultValue: "black",
            },
            {
                name: fields.buttonAlignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.buttonWidth,
                editorTemplate: "TextField",
                displayName: "Button Width(%)",
                defaultValue: "100%",
            },
        ],
        group: "Common",
    },
};

export default widgetModule;